import { Button, Modal, Row, Space, Spin, Table, Typography } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent.jsx";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteTeamFromSeason, useFetchSeasonTeams, useFetchTeamFixtures } from "../../entities/api";
import { PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "~hooks/useHandlePagination.js";
import usePermissionsByCompetitionName from "../../hooks/usePermissionsByCompetitionName";
import AddTeamToSeasonModal from "./AddTeamToSeasonModal";
import { getTeamInSeasonColumns } from "./columnsRepository";

const SeasonTeams = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { formatMessage } = useIntl();
  const [filteredData, setFilteredData] = useState([]);
  const { customerId } = useParams();
  const [isAddTeamToSeasonModalOpen, setIsAddTeamToSeasonModalOpen] = useState(!!state?.entityName);
  const params = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(50);
  const { data, isLoading } = useFetchSeasonTeams({
    include: "organization,fixtures,entities,seasons,conferences,divisions",
    limit: pageSize,
    offset,
  });
  const nextLink = useMemo(() => data?.links?.next, [data?.links?.next]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const {
    refetch,
    data: fixturesList,
    isLoading: isFixturesLoading,
  } = useFetchTeamFixtures({
    teamId: selectedTeamId,
  });
  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteTeamFromSeason();

  const pageRef = useRef();

  useEffect(() => {
    if (!data) {
      return;
    }

    const filteredData = data.data;
    const addConferenceAndDivisionsData = (result) => {
      const { divisions, conferences } = data.includes.resources;
      if (divisions && conferences) {
        return {
          conference: conferences[result.conferenceId],
          division: divisions[result.divisionId],
        };
      }
    };
    filteredData.forEach((result, index) => {
      filteredData[index] = {
        ...result,
        ...data.includes?.resources?.entities?.[result.entity.id],
        ...addConferenceAndDivisionsData(result),
      };
      filteredData[index].status = result.status;
    });
    setFilteredData(filteredData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (selectedTeamId) {
      refetch();
    }
  }, [refetch, selectedTeamId]);

  const handleDeleteIntent = (entityId) => {
    setSelectedTeamId(entityId);
    openModal();
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTeamId(null);
  };

  const handleDelete = useCallback(async () => {
    await deleteFn(selectedTeamId);
    closeModal();
  }, [deleteFn, selectedTeamId]);

  const { seasonEntityPermissionName } = usePermissionsByCompetitionName();

  return (
    <>
      <Modal
        open={isModalOpen}
        closable={true}
        onCancel={closeModal}
        okText={formatMessage({
          id: "delete",
          defaultMessage: "Delete",
        })}
        onOk={handleDelete}
        okButtonProps={{
          disabled: fixturesList?.meta?.count || isFixturesLoading,
          loading: isDeletePending,
        }}
        okType={"danger"}
      >
        {isFixturesLoading ? (
          <Row justify={"center"}>
            <Spin/>
          </Row>
        ) : fixturesList?.meta?.count ? (
          formatMessage({
            id: "delete.denied.season.team",
            defaultMessage: "Unable to delete, the team still has active fixtures in the season",
          })
        ) : (
          formatMessage({
            id: "delete.item.confirm",
            defaultMessage: "You are about to delete this item. Are you sure?",
          })
        )}
      </Modal>
      <AddTeamToSeasonModal
        isOpen={isAddTeamToSeasonModalOpen}
        setIsOpen={setIsAddTeamToSeasonModalOpen}
        pageRef={pageRef}
        reloadData={refetch}
      />
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Typography.Title level={4}>
            {`
              ${formatMessage({
                id: "teams",
                defaultMessage: "Teams",
              })}
            `}
          </Typography.Title>
          <Space>
            <PermissionCheck
              permissionKeys={[seasonEntityPermissionName]}
              permissionOptions={[PermissionOperations.CREATE]}
            >
              <Button
                type="primary"
                icon={<i className={"fas fa-plus"} style={{ marginRight: "5px" }}/>}
                onClick={() => setIsAddTeamToSeasonModalOpen(true)}
              >
                <FormattedMessage id="team.add" defaultMessage="Add Team to Season"/>
              </Button>
            </PermissionCheck>
          </Space>
        </Space>

        <Button
          icon={<i style={{ paddingRight: "5px" }} className="fas fa-images"/>}
          onClick={() =>
            navigate(
              `/${customerId}/admin/` +
              params.sport +
              "/" +
              params.organizationId +
              "/seasons/" +
              params.seasonId +
              "/entity-images",
            )
          }
        >
          <FormattedMessage id="images.manage" defaultMessage="Manage Images"/>
        </Button>
        <Table
          size="small"
          columns={getTeamInSeasonColumns({
            formatMessage,
            customerId,
            sport: params.sport,
            handleDeleteIntent,
            seasonEntityPermissionName,
          })}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions,
            showQuickJumper: false,
            total: nextLink ? offset + filteredData.length + 1 : filteredData.length + offset,
            position: ["bottomCenter"],
            onChange: handlePaginationChange,
            showTotal: (total, range) => (
              <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range}/>
            ),
          }}
          scroll={{ x: true }}
          rowKey={"entityId"}
        />
      </Space>
    </>
  );
};

export default SeasonTeams;
